import { WOMEN_APPAREL_PATH_IDENTIFIER } from "@faire/retailer-shared/categories/pathIdentifier";
import { SHOWROOM_CATEGORY_URL_ID } from "@faire/retailer-shared/categories/urlId";
import { useCategoryStore } from "@faire/retailer-visitor-shared/stores/domain/useCategoryStore";
import { findActiveCategoryByUrlIds } from "@faire/retailer-visitor-shared/stores/domain/CategoryStore/findByUrlId";
import {
  LOGGED_OUT_CATEGORY_PAGE_APPAREL_FAQS,
  trackFaqClick,
} from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageBelowResults/LoggedOutCategoryPageFAQs/__internal__/LoggedOutCategoryPageApparelFAQsContent";
import { getC1CategoryFAQs } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageBelowResults/LoggedOutCategoryPageFAQs/__internal__/LoggedOutCategoryPageFAQsContent";
import { VisibilityTracker } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/VisibilityTracker";
import { useLoggedOutCategoryPageCurrentCategory } from "@faire/retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/Context/useLoggedOutCategoryPageCurrentCategory";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Accordion } from "@faire/web/slate/Accordion";
import { Spacer } from "@faire/web/slate/spacing";
import { trackSeoLoggedOutCategoryPageC1Click } from "@faire/web-api--source/events/seo/click/loggedOutCategoryPageC1";
import isChromatic from "chromatic/isChromatic";
import shuffle from "lodash/shuffle";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";

const MAX_NUMBER_OF_C2 = 5;

export const LoggedOutCategoryPageFAQs: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const { categories } = useCategoryStore();
  const categoryModel = useLoggedOutCategoryPageCurrentCategory();
  const history = useHistory();

  const categoryUrlIds = categoryModel?.urlIdStrings ?? [];
  const showApparelFAQs =
    categoryModel?.mostSpecificPathIdentifier === WOMEN_APPAREL_PATH_IDENTIFIER;
  const isShowroomCategoryRoute =
    categoryModel?.urlId?.id === SHOWROOM_CATEGORY_URL_ID;

  const category = findActiveCategoryByUrlIds(categoryUrlIds, categories);

  const subCategories = useMemo(() => {
    const subCategories = category?.sub_categories;
    if (!subCategories?.length) {
      return [];
    }
    const shuffledSubCategories = isChromatic()
      ? subCategories
      : shuffle(subCategories);
    return shuffledSubCategories.slice(
      0,
      Math.min(MAX_NUMBER_OF_C2, shuffledSubCategories.length)
    );
  }, [category?.sub_categories]);

  const C1CategoryFAQs = useMemo(() => {
    if (!category) {
      return [];
    }
    return getC1CategoryFAQs(history, category, subCategories, strictLocalize);
  }, [history, category, strictLocalize, subCategories]);

  const shouldRenderFAQ = useMemo(() => {
    // only C1 will have FAQs
    return (
      showApparelFAQs ||
      (categoryModel?.isPrimaryCategory &&
        !categoryModel?.isAllBrands &&
        !isShowroomCategoryRoute)
    );
  }, [
    showApparelFAQs,
    categoryModel?.isPrimaryCategory,
    categoryModel?.isAllBrands,
    isShowroomCategoryRoute,
  ]);

  const LOGGED_OUT_CATEGORY_PAGE_APPAREL_FAQS_OBJECT = useMemo(() => {
    return LOGGED_OUT_CATEGORY_PAGE_APPAREL_FAQS(history);
  }, [history]);

  if (!shouldRenderFAQ || category === undefined) {
    return <Spacer height="5x" />;
  }

  if (showApparelFAQs) {
    return (
      <VisibilityTracker name="apparel-faq">
        <Spacer height="10x" />
        <Accordion
          items={LOGGED_OUT_CATEGORY_PAGE_APPAREL_FAQS_OBJECT}
          onItemClick={trackFaqClick}
        />
        <Spacer height="10x" />
      </VisibilityTracker>
    );
  } else {
    return (
      <>
        <Spacer height="10x" />
        <Accordion
          initiallyOpenIndex={0}
          items={C1CategoryFAQs}
          onItemClick={(index: number) =>
            trackSeoLoggedOutCategoryPageC1Click(index.toString())
          }
        />
        <Spacer height="10x" />
      </>
    );
  }
};
